<template>
  <section class="row">
    <div
      v-if="!isLoading"
      class="col-12"
    >
      <div class="row">
        <div class="col-2 my-auto">
          <v-icon
            icon="chevron-left"
            class="cursor-pointer"
            @click="() => $router.back()"
          />
        </div>
        <div class="col-8 h3 my-auto text-center">
          Facture {{ productName }}
        </div>
        <div
          v-if="contract.documents && contract.documents.length > 0"
          class="col-2 my-auto text-center"
        >
          <router-link
            v-slot="{ navigate }"
            :to="`/client/contracts/${contractRouteId}`"
            custom
          >
            <div>
              <div
                class="rounded-circle bg-blue-700 d-flex mx-auto cursor-pointer"
                style="height:45px; width:45px; min-width:45px"
                @click="navigate"
              >
                <div class="d-flex m-auto">
                  <v-icon
                    folder="common/svg"
                    icon="contract-white"
                    width="24"
                    height="24"
                  />
                </div>
              </div>
              <div
                class="fw-bold mt-1 text-blue-700 cursor-pointer d-none d-md-inline-block"
                @click="navigate"
              >
                Votre contrat
              </div>
            </div>
          </router-link>
        </div>
        <div class="col-12 text-center mt-2 mt-md-0 mb-3">
          <div class="lead">
            Contrat N° {{ invoice.subscriptionReference }}
          </div>
          <div class="my-1">
            Facture N° {{ invoice.reference }}
          </div>
          <div
            class="badge rounded-pill"
            :class="`bg-${getStatus.color} text-${getStatus.textColor || 'blue-700'}`"
          >
            {{ getStatus.text }}
          </div>
        </div>
        <div class="col-12">
          <div class="row justify-content-xxl-between justify-content-center">
            <div
              v-for="(card, index) in cards"
              :key="card.title"
              class="col-auto"
            >
              <v-card
                :index="index"
                :title="card.title"
                :color="card.color"
                :icon="card.icon"
                :content="card.getter()"
                class="mb-3"
              />
            </div>
          </div>
        </div>
        <div class="col-12">
          <div
            v-if="late.length > 0"
            class="my-3 h5"
          >
            Paiement{{ late.length > 1 ? 's' : '' }} en retard,
            échoué{{ late.length > 1 ? 's' : '' }} ou contesté{{ late.length > 1 ? 's' : '' }}
          </div>
          <v-display
            v-for="(payment) in late"
            :key="payment.id"
            :payment="payment"
          />
          <div
            v-if="inProgress.length > 0"
            class="my-3 h5"
          >
            Paiements en cours
          </div>
          <v-display
            v-for="(payment) in inProgress"
            :key="payment.id"
            :payment="payment"
          />
          <div
            v-if="toBePaid.length > 0"
            class="my-3 h5"
          >
            Prochaines échéances
          </div>
          <v-display
            v-for="(payment) in toBePaid"
            :key="payment.id"
            :payment="payment"
          />
          <div
            v-if="alreadyPaid.length > 0"
            class="my-3 h5"
          >
            Historique de paiement
          </div>
          <v-display
            v-for="(payment) in alreadyPaid"
            :key="payment.id"
            :payment="payment"
          />
          <div
            v-if="payments.length === 0"
            style="font-weight:600"
          >
            Aucun paiement
          </div>
        </div>
      </div>
    </div>
    <v-loading-animation
      v-else
      color="blue-500"
      class="mt-5"
    />
  </section>
</template>
<script>
/* eslint-disable object-curly-newline */
import { mapGetters } from 'vuex';
import { formatPrice } from '@/utils/_Number';
import { formatDate } from '@/utils/_Date';
import { getContractRouteId } from '@/utils/contractRoute';

import LoadingAnimation from '@/components/common/helpers/LoadingAnimation.vue';

import InvoiceLargeCard from '@/components/invoices/cards/InvoiceLargeCard.vue';
import PaymentDisplay from '@/components/invoices/helpers/PaymentDisplay.vue';

export default {
  name: 'ClientInvoice',
  components: {
    'v-card': InvoiceLargeCard,
    'v-display': PaymentDisplay,
    'v-loading-animation': LoadingAnimation,
  },
  data() {
    return {
      isLoading: true,
      invoice: {
        status: 'PAID',
        payments: [],
      },
      status: [
        { id: 'PENDING', text: 'En attente', color: 'yellow-600' },
        { id: 'CHECKED_OUT', text: 'En attente', color: 'yellow-600' },
        { id: 'IN_PROGRESS', text: 'En cours', color: 'blue-400', textColor: 'white' },
        { id: 'PAID', text: 'Payée', color: 'green-400' },
        { id: 'CANCELLED', text: 'Résiliée', color: 'pink-700', textColor: 'white' },
        { id: 'FAILED', text: 'Échoué', color: 'pink-700', textColor: 'white' },
        { id: 'TERMINATED', text: 'Arrêtée', color: 'pink-700', textColor: 'white' },
        { id: 'OVERDUE', text: 'En retard', color: 'pink-700', textColor: 'white' },
        { id: 'DISPUTE', text: 'Contesté', color: 'pink-700', textColor: 'white' },
      ],
      cards: [
        { title: 'Fractionnement', color: 'blue', getter: this.getInvoiceFrequency, icon: { display: false } },
        { title: 'Période', color: 'yellow', getter: this.getInvoicePeriod, icon: { display: false } },
        { title: 'Prime TTC', color: 'green', getter: this.getInvoiceAnnualPremium, icon: { display: false } },
        { title: 'Paiement en retard', color: 'pink', getter: this.getInvoicePaymentsLate, icon: { display: true, name: 'bills', folder: 'dashboard/svg/invoice' } },
      ],
    };
  },
  computed: {
    ...mapGetters({
      contracts: 'contracts/get',
    }),
    contract() {
      return this.contracts
        .find(({ reference }) => reference === this.invoice.subscriptionReference)
       || { documents: [] };
    },
    contractRouteId() {
      return getContractRouteId(this.contract);
    },
    annual() {
      return this.invoice.plan === 'ANNUAL';
    },
    getStatus() {
      return this.status.find(({ id }) => id === this.invoice.status)
        || { color: 'primary', text: '' };
    },

    late() {
      if (this.payments) {
        return this.payments
          .filter(({ status }) => ['OVERDUE', 'FAILED', 'DISPUTE'].includes(status))
          .sort(this.byDate);
      }
      return [];
    },
    inProgress() {
      if (this.payments) {
        return this.payments
          .filter(({ status }) => ['IN_PROGRESS'].includes(status))
          .sort(this.byDate);
      }
      return [];
    },
    toBePaid() {
      if (this.payments) {
        return this.payments
          .filter(({ status }) => ['PENDING'].includes(status))
          .sort(this.byDate);
      }
      return [];
    },
    alreadyPaid() {
      if (this.payments) {
        return this.payments.filter(({ status }) => ['PAID', 'CANCELLED'].includes(status))
          .sort(this.byDate);
      }
      return [];
    },

    payments() {
      return this.invoice.payments
        .filter(({ amount }) => amount > 0)
        .filter(({ visible }) => visible);
    },
    annualTitle() {
      if (this.annual) {
        return `depuis le ${formatDate(this.invoice.effectiveDate)}`;
      }
      return '';
    },
    productName() {
      return (this.$store.getters['rater/products']
        .find((product) => product.id === this.invoice.productId) || {})
        .name;
    },
  },
  mounted() {
    const { id } = this.$route.params;
    this.$store.dispatch('invoices/findInvoiceItemById', id)
      .then((invoice) => { this.invoice = invoice })
      .finally(() => { this.isLoading = false });
  },
  methods: {
    getInvoiceFrequency() {
      return [this.invoice.plan === 'ANNUAL' ? 'Annuel' : 'Mensuel'];
    },
    getInvoicePeriod() {
      const {
        effectiveDate,
        endEffectiveDate,
      } = this.invoice;
      return [formatDate(effectiveDate), '•', formatDate(endEffectiveDate)];
    },
    getInvoiceAnnualPremium() {
      return [`${formatPrice(this.invoice.annualPremiumTaxesIncl, true)} €`];
    },
    getInvoicePaymentsLate() {
      return [(this.invoice.payments
        .filter(({ status }) => status === 'OVERDUE' || status === 'FAILED' || status === 'DISPUTE') || [])
        .length];
    },
    byDate(invoiceA, invoiceB) {
      const a = new Date(invoiceA.startDate).getTime();
      const b = new Date(invoiceB.startDate).getTime();
      if (a - b === 0) {
        return 0;
      }
      return a - b;
    },
  },
};
</script>
